import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { each } from 'underscore';
import { TabList, Tab } from '@fluentui/react-components';
import { useJsData } from '@bingads-webui-cc-react/js-data-rest';
import { AddVideoPanelContent, InvestVideoPanelContent, isValidXandrAccount } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useSourceVideos } from './hooks/use-source-videos';
import { AdsStudioAddAssetPanel } from './add-asset-panel';
import { getAddVideoError } from './error/add-video-error';

export const MixedVideoPanel = (props) => {
  const {
    i18n,
    aid, cid, oDataConfig,
    dataService, logger, notificationApi, navigation, open, perfMarker,
    permissions,
  } = props;
  const [selectedTab, setSelectedTab] = useState('url');
  const isMultiSelection = useMemo(() => selectedTab !== 'invest', [selectedTab]);
  const isXandrIntEnabled = useMemo(() => permissions.dynamic.XandrIntegrationEnabled, [permissions]);
  // eslint-disable-next-line no-restricted-globals
  const { data: account } = useJsData(jsData => (isXandrIntEnabled ? jsData.Account.find(aid) : Promise.resolve()), [aid, isXandrIntEnabled]);
  const isXandrIntegrationEnabled = useMemo(() => (isXandrIntEnabled ? isValidXandrAccount(account) : false), [account, isXandrIntEnabled]);

  const {
    setSelectedVideos, onUrlClickSelect, onInvestClickSelect, showBlocker, selectedVideos,
  } = useSourceVideos({
    dataService, logger, navigation, perfMarker,
  });

  const dismiss = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleJobErrors = useCallback((jobErrors) => {
    if (jobErrors.length > 0) {
      each(jobErrors, (error) => {
        const errorMsg = getAddVideoError({
          error,
          i18n,
          aid,
          logger,
        });
        notificationApi.showWarning(errorMsg);
      });
    }
  }, [aid, i18n, logger, notificationApi]);

  const onClickSelect = useCallback(() => {
    if (selectedTab === 'invest') {
      onInvestClickSelect(handleJobErrors);
    } else {
      onUrlClickSelect(handleJobErrors);
    }
  }, [handleJobErrors, onUrlClickSelect, onInvestClickSelect, selectedTab]);

  const onTabSelect = useCallback((event, data) => {
    setSelectedVideos([]);
    setSelectedTab(data.value);
  }, [setSelectedVideos]);

  useEffect(() => {
    if (selectedTab === 'invest' && selectedVideos && selectedVideos.length === 1) {
      onInvestClickSelect(handleJobErrors);
    }
  }, [handleJobErrors, selectedVideos, selectedTab]);

  return (
    <AdsStudioAddAssetPanel
      aid={aid}
      i18n={i18n}
      dismiss={dismiss}
      isOpen={open}
      showBlocker={showBlocker}
      selectedAsset={selectedVideos}
      headerText={i18n.getString(_TL_('Add video'))}
      logger={logger}
      notificationApi={notificationApi}
      onClickSelect={onClickSelect}
      isMultiSelection={isMultiSelection}
    >
      <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
        <Tab value="url">{i18n.getString(_TL_('Upload'))}</Tab>
        {isXandrIntegrationEnabled && <Tab value="invest">{i18n.getString(_TL_('Microsoft Invest'))}</Tab>}
      </TabList>
      {selectedTab === 'url' && <AddVideoPanelContent i18n={i18n} onChange={setSelectedVideos} />}
      {selectedTab === 'invest' && isXandrIntegrationEnabled && <InvestVideoPanelContent i18n={props.i18n} aid={aid} cid={cid} oDataConfig={oDataConfig} setSelectedVideos={setSelectedVideos} logger={logger} />}
    </AdsStudioAddAssetPanel>
  );
};

MixedVideoPanel.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  navigation: PropTypes.shape({ gotoImagesPage: PropTypes.func }).isRequired,
  dataService: PropTypes.shape({
    uploadVideoByUrl: PropTypes.func,
    uploadInvestVideo: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      XandrIntegrationEnabled: PropTypes.bool,
    }),
  }),
};

MixedVideoPanel.defaultProps = {
  permissions: { dynamic: { XandrIntegrationEnabled: false } },
};
